import type { FC, FormEvent } from 'react';
import React, { useCallback, useState } from 'react';

import { Root } from './styles';
import { useAppContext, useTranslations, useUserData } from '../../hooks';
import { BackInStockNotificationChannel } from '../../models';
import { Loading } from '../Loading';
import { NotifyForm } from '../NotifyForm';
import { NotifyType } from '../NotifyType';
import { PopupHeader } from '../PopupHeader';
import { RequestFailed } from '../RequestFailed';
import { Thanks } from '../Thanks';

interface NotifyCumulusProps {
  productCode: string;
}

export const NotifyCumulus: FC<NotifyCumulusProps> = ({ productCode }) => {
  const { notifyMeSubscriptionConfiguration } = useAppContext();
  const { translations } = useTranslations();
  const [currentType, setCurrentType] = useState(BackInStockNotificationChannel.Email);
  const [inputData, setInputData] = useState('');
  const [isReserved, setIsReserved] = useState(false);
  const { userData, loading, error } = useUserData({ currentType, setInputData });

  const onChangeData = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (!notifyMeSubscriptionConfiguration.IsEditionEnabled) {
        return;
      }

      const trimmedValue = event.currentTarget.value.trim();

      setInputData(trimmedValue);
    },
    [notifyMeSubscriptionConfiguration.IsEditionEnabled],
  );

  const onChangeType = useCallback(
    (event: FormEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;

      setCurrentType(value as BackInStockNotificationChannel);
      if (userData.email && value === BackInStockNotificationChannel.Email) {
        setInputData(userData.email);
      } else if (userData.telephoneNumber && value === BackInStockNotificationChannel.Sms) {
        setInputData(userData.telephoneNumber);
      }
    },
    [userData.email, userData.telephoneNumber],
  );

  const onSubmit = useCallback((): void => {
    setIsReserved(true);
  }, []);

  if (loading) {
    return (
      <Root>
        <Loading />
      </Root>
    );
  }

  if (error) {
    return (
      <Root>
        <RequestFailed />
      </Root>
    );
  }

  return (
    <Root>
      {isReserved ? (
        <Thanks currentType={currentType} />
      ) : (
        <>
          <PopupHeader title={translations.notifyMe} />
          <NotifyType
            currentType={currentType}
            onChange={onChangeType}
          />
          <NotifyForm
            currentType={currentType}
            inputData={inputData}
            productCode={productCode}
            onChange={onChangeData}
            onSubmit={onSubmit}
          />
        </>
      )}
    </Root>
  );
};
